import { createReducer, on } from '@ngrx/store';
import {
  CategoryApiActions,
  CategoryGeoLocationsActions,
  CategoryListActions,
  CategorySignalRHubActions,
  CategoryStatusContainerActions,
  CategoryViewActions,
} from './category.actions';
import {
  adapter,
  CategoryState,
  initialState,
  isCategoryEntityType,
  selectId,
  selectIdFromKeys,
} from './category.state';

function updateRoomCheckedInStatus(state: CategoryState, roomId: string, isCheckedIn: boolean): CategoryState {
  const existing = state.entities[roomId];
  if (!existing || !isCategoryEntityType(existing, 'Room') || !existing.entity) return state;
  const updatedRoom = { ...existing.entity, isCheckedIn };
  const updatedCategory = existing.withUpdatedEntity(updatedRoom);
  return adapter.setOne(updatedCategory, state);
}

export const categoryReducer = createReducer(
  initialState,
  // API loaded
  on(CategoryApiActions.categoriesLoaded, (state, { configurations }) => adapter.setMany(configurations, state)),
  // API updated
  on(CategoryApiActions.categoryUpdated, (state, { configuration }) => adapter.setOne(configuration, state)),
  // API updated error
  on(CategoryApiActions.categoryUpdatedError, (state, { originalConfiguration }) =>
    adapter.setOne(originalConfiguration, state)
  ),
  // API deleted
  on(CategoryApiActions.categoryDeleted, (state, { configuration }) =>
    adapter.removeOne(selectId(configuration), state)
  ),
  // API deleted error
  on(CategoryApiActions.categoryDeletedError, (state, { configuration }) => adapter.setOne(configuration, state)),
  // API added
  on(CategoryApiActions.categoryAdded, (state, { configuration, originalConfiguration }) => {
    // Remove the optimistic entry if it exists, then add the server response
    // This prevents duplicates when the server generates a new uniqueId
    const stateWithoutOptimistic = originalConfiguration
      ? adapter.removeOne(selectId(originalConfiguration), state)
      : state;
    return adapter.setOne(configuration, stateWithoutOptimistic);
  }),
  // API added error
  on(CategoryApiActions.categoryAddedError, (state, { configuration }) =>
    adapter.removeOne(selectId(configuration), state)
  ),
  // View added
  on(CategoryViewActions.categoryAdded, (state, { entity }) => {
    return adapter.setOne(entity, state);
  }),
  // View updated
  on(CategoryViewActions.categoryUpdated, (state, { entity }) => {
    return adapter.setOne(entity, state);
  }),
  // View deleted
  on(CategoryViewActions.categoryDeleted, (state, { entity }) => {
    return adapter.removeOne(selectId(entity), state);
  }),
  // List updated (Location)
  on(CategoryListActions.categoryUpdated, (state, { entity }) => {
    return adapter.setOne(entity, state);
  }),
  // List added (Location)
  on(CategoryListActions.categoryAdded, (state, { entity }) => {
    return adapter.setOne(entity, state);
  }),
  // List deleted (Location)
  on(CategoryListActions.categoryDeleted, (state, { entity }) => {
    return adapter.removeOne(selectId(entity), state);
  }),
  // Geo locations updated (Location)
  on(CategoryGeoLocationsActions.categoryUpdated, (state, { entity }) => {
    return adapter.setOne(entity, state);
  }),
  // Status changed optimistically (Room check in/out) - updates immediately before API call
  on(CategoryStatusContainerActions.statusChanged, (state, { room, status }) =>
    updateRoomCheckedInStatus(state, selectIdFromKeys('Room', room.uniqueId), status)
  ),
  // Status changed (Room check in/out) - updates after API call succeeds
  on(CategoryApiActions.statusChanged, (state, { categoryType, categoryId, status }) => {
    if (categoryType === 'Room') {
      return updateRoomCheckedInStatus(state, selectIdFromKeys(categoryType, categoryId), status);
    }
    return state;
  }),
  // SignalR Hub updated (Room status update)
  on(CategorySignalRHubActions.categoryUpdated, (state, { categoryType, categoryId, status }) => {
    if (categoryType === 'Room') {
      return updateRoomCheckedInStatus(state, selectIdFromKeys(categoryType, categoryId), status === 'CheckedIn');
    }
    return state;
  })
);

const { selectIds, selectEntities, selectAll } = adapter.getSelectors();

// Internal selectors - used by category.selector.ts
export const selectCategoryIds = selectIds;
export const selectCategoryEntities = selectEntities;
export const selectAllCategories = selectAll;
